import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

function fmtClock(ms) {
  const sec = Math.max(0, Math.round((ms || 0) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Loads the captured frames as object URLs for the filmstrip, revoking them
 *  on unmount / draft change / reload. Returns { thumbs, reload }. */
function useFilmstrip(controller) {
  const [thumbs, setThumbs] = useState([]);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const urls = [];
    controller.getCapturedFrames().then((frames) => {
      if (cancelled || !frames) return;
      const next = frames.map((f) => {
        const url = URL.createObjectURL(f.blob);
        urls.push(url);
        return { tMs: f.tMs, url };
      });
      setThumbs(next);
    });
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [controller, controller.draftId, version]);
  return { thumbs, reload: () => setVersion((v) => v + 1) };
}

/**
 * Review-your-take screen shown after a recording stops. Compile is a
 * deliberate action here — the user can optionally transcribe the narration
 * to preview what the mic heard, re-record if they're not happy, or discard.
 * Mirrors the prototype, where stopping a recording never auto-compiles.
 */
export const CapturedStage = observer(function CapturedStage({ controller }) {
  const meta = controller.capturedMeta || {};
  const frameCount = meta.frameCount || 0;
  const hasAudio = !!meta.hasAudio;
  const transcript = controller.transcript;
  const transcribing = controller.transcribing;
  const err = controller.error;
  const { thumbs, reload } = useFilmstrip(controller);
  const [zoomUrl, setZoomUrl] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);

  // Esc closes the zoom overlay.
  useEffect(() => {
    if (!zoomUrl) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setZoomUrl(null); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [zoomUrl]);

  const toggleSelect = (index, shiftKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftKey && anchor !== null) {
        const [lo, hi] = anchor <= index ? [anchor, index] : [index, anchor];
        for (let j = lo; j <= hi; j++) next.add(thumbs[j].tMs);
      } else {
        const tMs = thumbs[index].tMs;
        if (next.has(tMs)) next.delete(tMs); else next.add(tMs);
      }
      return next;
    });
    setAnchor(index);
  };

  const updateLine = (i, text) =>
    controller.updateTranscript(transcript.map((s, idx) => (idx === i ? { ...s, text } : s)));
  const removeLine = (i) =>
    controller.updateTranscript(transcript.filter((_, idx) => idx !== i));
  const addLine = () => {
    const lastT = transcript.length ? transcript[transcript.length - 1].t_ms : 0;
    controller.updateTranscript([...transcript, { t_ms: lastT, text: "" }]);
  };

  const onDeleteSelected = async () => {
    // Delete in timeline order, independent of selection order.
    const tsList = thumbs.map((t) => t.tMs).filter((tMs) => selected.has(tMs));
    await controller.deleteCapturedFrames(tsList);
    setSelected(new Set());
    reload();
  };

  const onDeleteOne = async (tMs) => {
    await controller.deleteCapturedFrames([tMs]);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(tMs);
      return next;
    });
    reload();
  };

  const doDiscard = () => {
    Promise.resolve(controller.discardDraft(controller.draftId)).then(() =>
      controller.closeWizard(),
    );
  };

  return (
    <div className="cua-captured-stage">
      {thumbs.length > 0 && (
        <>
          {selected.size > 0 && (
            <div className="cua-captured-selbar">
              <span>{selected.size} selected</span>
              <button
                type="button"
                className="cua-captured-transcribe-link"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--danger"
                onClick={onDeleteSelected}
              >
                Delete {selected.size}
              </button>
            </div>
          )}
          <div className="cua-captured-filmstrip">
            {thumbs.map((t, i) => (
              <figure
                key={i}
                className={`cua-captured-frame${selected.has(t.tMs) ? " cua-captured-frame--selected" : ""}`}
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={selected.has(t.tMs)}
                  className={`cua-captured-frame-sel${selected.has(t.tMs) ? " is-on" : ""}`}
                  aria-label={`Select frame at ${fmtClock(t.tMs)}`}
                  title="Select (shift-click for a range)"
                  onClick={(e) => toggleSelect(i, e.shiftKey)}
                >
                  <span aria-hidden="true">✓</span>
                </button>
                <button
                  type="button"
                  className="cua-captured-frame-del"
                  aria-label={`Delete frame at ${fmtClock(t.tMs)}`}
                  title="Delete this frame"
                  onClick={() => onDeleteOne(t.tMs)}
                >
                  ✕
                </button>
                <img
                  src={t.url}
                  alt={`Frame at ${fmtClock(t.tMs)}`}
                  title="Click to enlarge"
                  onClick={() => setZoomUrl(t.url)}
                />
                <figcaption>{fmtClock(t.tMs)}</figcaption>
              </figure>
            ))}
          </div>
        </>
      )}

      <div className="cua-captured-summary">
        <span className="cua-captured-stat">{fmtClock(meta.durationMs)}</span>
        <span className="cua-captured-sep">·</span>
        <span className="cua-captured-stat">
          {frameCount} frame{frameCount === 1 ? "" : "s"}
        </span>
        <span className="cua-captured-sep">·</span>
        <span className="cua-captured-stat">
          {hasAudio ? "🎙 narration captured" : "no narration"}
        </span>
      </div>

      <div className="cua-captured-narration">
        {hasAudio ? (
          transcript ? (
            <div className="cua-captured-transcript">
              {transcript.length === 0 && (
                <div className="cua-captured-transcript-empty">
                  No speech was detected — add lines below if you want to guide the compile.
                </div>
              )}
              {transcript.map((seg, i) => (
                <div key={i} className="cua-captured-transcript-line">
                  <span className="cua-captured-transcript-t">{fmtClock(seg.t_ms)}</span>
                  <input
                    className="cua-captured-transcript-input"
                    value={seg.text}
                    aria-label={`Narration at ${fmtClock(seg.t_ms)}`}
                    onChange={(e) => updateLine(i, e.target.value)}
                  />
                  <button
                    type="button"
                    className="cua-captured-transcript-del"
                    aria-label="Remove line"
                    title="Remove line"
                    onClick={() => removeLine(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="cua-captured-transcribe-link" onClick={addLine}>
                + Add line
              </button>
            </div>
          ) : (
            <div className="cua-captured-transcribe">
              <button
                type="button"
                className="cua-captured-transcribe-link"
                disabled={transcribing}
                onClick={() => controller.transcribe()}
              >
                {transcribing ? "Transcribing…" : "Transcribe"}
              </button>
              <span className="cua-captured-optional-hint">
                Optional — compile transcribes the narration automatically.
              </span>
            </div>
          )
        ) : (
          <div className="cua-captured-hint">
            No microphone narration — this will compile from screenshots only.
          </div>
        )}
      </div>

      {err && (
        <div className="cua-captured-error" role="alert">
          {err.message || err.code}
        </div>
      )}

      <div className="cua-captured-actions">
        <button
          type="button"
          className="copilot-modal-btn copilot-modal-btn--danger"
          onClick={doDiscard}
        >
          Discard
        </button>
        <button
          type="button"
          className="copilot-modal-btn copilot-modal-btn--ghost"
          onClick={() => controller.reRecord()}
        >
          ↻ Re-record
        </button>
        <button
          type="button"
          className="copilot-modal-btn copilot-modal-btn--primary"
          disabled={transcribing}
          onClick={() => controller.compile()}
        >
          Compile →
        </button>
      </div>

      {zoomUrl && (
        <div
          className="cua-captured-zoom"
          role="dialog"
          aria-label="Frame preview"
          onClick={() => setZoomUrl(null)}
        >
          <img src={zoomUrl} alt="Enlarged frame" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
});
